import { z } from 'zod';

import {
  EnumOrganizationMemberPermissions,
  EnumOrganizationPermissions,
} from '@/helpers/enums/permission.enum';

const PathFuncSchema = z.record(z.coerce.number());

type PathFuncParam = z.infer<typeof PathFuncSchema>;

export const adminEnumRoutes = {
  root: {
    path: '/admin',
    name: 'Admin',
    meta: {
      title: 'sidemenu.dashboard',
      requiresAuth: true,
      isAdministrator: true,
    },
  },
  dashboard: {
    path: '/admin/dashboard',
    name: 'Dashboard',
    meta: {
      title: 'sidemenu.dashboard',
      icon: 'mdi-view-dashboard-outline',
      requiresAuth: true,
      isAdministrator: true,
      memberPermissionsAny: [
        EnumOrganizationMemberPermissions.ViewUsersActivity,
        EnumOrganizationMemberPermissions.ViewUsers,
        EnumOrganizationMemberPermissions.ViewSales,
        // KP-61: department managers land here too — the dashboard hosts
        // their "My departments" section. Nobody holds this permission
        // outside internal-team orgs, so other tenants are unaffected.
        EnumOrganizationMemberPermissions.ManageDepartment,
      ],
    },
  },
  /** >>>>>>>>>>>>>>> User Routes <<<<<<<<<<<<<<< **/
  users: {
    path: '/admin/users',
    name: 'Users View',
    meta: {
      title: 'sidemenu.users',
      icon: 'mdi-account',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsers],
    },
  },
  createUser: {
    path: '/admin/users/create',
    name: 'Create User',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ModifyUsers],
    },
  },
  userView: {
    path: '/admin/users',
    name: 'User View',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsers],
    },
  },
  userDetails: {
    path: '/admin/users/:userId/details',
    name: 'User Details',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsers],
    },
  },
  userCourses: {
    path: '/admin/users/:userId/courses',
    name: 'User Courses',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsersActivity],
    },
  },
  courseReviews: {
    path: '/admin/courses/mine/:courseId/reviews',
    name: 'Course Reviews',
    pathFn: ({ param: courseId }: PathFuncParam) =>
      `/admin/courses/mine/:courseId/${courseId}/reviews`,
    meta: {
      title: 'sidemenu.reviews',
    },
  },
  userExternalCourses: {
    path: '/admin/users/:userId/externalCourses',
    name: 'User External Courses',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsersActivity],
    },
  },
  userChecklist: {
    path: '/admin/users/:userId/checklist',
    name: 'User Checklist',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ReadSkillsDevelopment,
      ],
    },
  },
  // KP-7: per-user grading/results tab — internal-team mode only.
  userResults: {
    path: '/admin/users/:userId/results',
    name: 'User Results',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsersActivity],
      shouldHaveInternalTeamMode: true,
    },
  },
  /** >>>>>>>>>>>>>>> Journey Routes <<<<<<<<<<<<<<< **/
  journeys: {
    path: '/admin/journey-planner',
    name: 'Journey View',
    meta: {
      title: 'sidemenu.journeyPlanner',
      icon: 'mdi-order-bool-ascending-variant',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ReadSkillsDevelopment,
      ],
    },
  },
  journeysOverview: {
    path: '/admin/journey-planner',
    name: 'Journey Overview',
    meta: {
      title: 'journeyPlanner.journeyPlannerOverview',
      icon: 'mdi-order-bool-ascending-variant',
    },
  },
  createJourneys: {
    path: '/admin/journey-planner/create',
    name: 'Create a Journey Planner',
    meta: {
      title: 'journeyPlanner.createJourneyTab',
    },
  },
  editJourney: {
    path: '/admin/journey-planner/:journeyId/edit',
    name: 'Journey create',
    meta: {
      title: 'journeyPlanner.createJourneyTab',
    },
  },
  journeyDetails: {
    path: '/admin/journey-planner/:journeyId/details',
    pathFn: ({ journeyId }: { journeyId: string | number }) =>
      `/admin/journey-planner/${journeyId}/details`,
    name: 'Journey Details',
    meta: {
      title: 'journeyPlanner.journeyPlannerDetails',
    },
  },
  journeyAssign: {
    path: '/admin/journey-planner/:journeyId/assign',
    pathFn: ({ journeyId }: { journeyId: string | number }) =>
      `/admin/journey-planner/${journeyId}/assign`,
    name: 'Journey Assign',
    meta: {
      title: 'journeyPlanner.journeyPlannerAssign',
    },
  },
  journeyItems: {
    path: '/admin/journey-planner/:journeyId/items',
    pathFn: ({ journeyId }: { journeyId: string | number }) =>
      `/admin/journey-planner/${journeyId}/details`,
    name: 'journeyPlanner Items',
    meta: {
      title: 'journeyPlanner.journeyPlannerItems',
    },
  },
  journeyItemsOverView: {
    path: '/admin/journey-planner/:journeyId/items/:itemId',
    name: 'Journey Item View',
    meta: {
      title: 'journeyPlanner.journeyPlannerItemView',
    },
  },
  journeyItemsEdit: {
    path: '/admin/journey-planner/:journeyId/items/:itemId/edit',
    name: 'Journey Item Edit',
    meta: {
      title: 'journeyPlanner.journeyPlannerEdit',
    },
  },
  createJourneysItem: {
    path: '/admin/journey-planner/:journeyId/items/create',
    name: 'New Journey Item',
    meta: {
      title: 'journeyPlanner.createJourneyTab',
    },
  },
  /** >>>>>>>>>>>>>>> Teams Routes <<<<<<<<<<<<<<< **/
  teams: {
    path: '/admin/teams',
    name: 'Teams View',
    meta: {
      title: 'sidemenu.teams',
      icon: 'mdi-account-group',
      // KP-61: no longer hiddenInInternalTeamMode — for internal-team orgs
      // this IS the Departments surface (label override renames it), with
      // the manager column on top of the existing group CRUD.
      memberPermissions: [
        EnumOrganizationMemberPermissions.ReadSkillsDevelopment,
      ],
    },
  },
  teamsOverview: {
    path: '/admin/teams/:teamId',
    pathFn: ({ teamId }: PathFuncParam) => `/admin/teams/${teamId}`,

    name: 'Team Overview',
    meta: {
      title: 'sidemenu.teams',
      icon: 'mdi-account-group',
    },
  },
  teamMembers: {
    path: '/admin/teams/:teamId/members',
    pathFn: ({ teamId }: PathFuncParam) => `/admin/teams/${teamId}/members`,
    name: 'Team Members',
    meta: {
      title: 'sidemenu.teams',
      icon: 'mdi-account-group',
    },
  },
  teamCourses: {
    path: '/admin/teams/:teamId/courses',
    pathFn: ({ teamId }: PathFuncParam) => `/admin/teams/${teamId}/courses`,
    name: 'Team Courses',
    meta: {
      title: 'sidemenu.teams',
      icon: 'mdi-account-group',
    },
  },
  createTeam: {
    path: '/admin/teams/create',
    name: 'Create Team',
    meta: {
      title: 'sidemenu.teams',
      icon: 'mdi-account-group',
    },
  },
  editTeam: {
    path: '/admin/teams/:teamId/edit',
    name: 'Edit Team',
    meta: {
      title: 'sidemenu.teams',
      icon: 'mdi-account-group',
    },
  },
  /** >>>>>>>>>>>>>>> Courses Routes <<<<<<<<<<<<<<< **/
  courses: {
    path: '/admin/courses',
    name: 'Courses View',
    meta: {
      title: 'sidemenu.courses',
      icon: 'mdi-bookshelf',
      memberPermissionsAny: [
        EnumOrganizationMemberPermissions.ViewCourses,
        EnumOrganizationMemberPermissions.ViewSpecializations,
        EnumOrganizationMemberPermissions.ViewImportedCourses,
        EnumOrganizationMemberPermissions.ViewExternalCourses,
      ],
    },
  },
  myCourses: {
    path: '/admin/courses/mine',
    name: 'My Courses View',
    meta: {
      title: 'sidemenu.myCourses',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  importedCourses: {
    path: '/admin/courses/imported',
    name: 'Imported Courses View',
    meta: {
      title: 'sidemenu.importedCourses',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewImportedCourses,
      ],
    },
  },
  externalCourses: {
    path: '/admin/courses/external',
    name: 'External Courses View',
    meta: {
      title: 'sidemenu.externalCourses',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewExternalCourses,
      ],
    },
  },

  externalCoursesOverview: {
    path: '/admin/courses/external/:externalCourseId',
    pathFn: ({ externalCourseId }: PathFuncParam) =>
      `/admin/courses/external/${externalCourseId}`,
    name: 'External Course Overview',
    meta: {
      title: 'sidemenu.externalCourses',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewExternalCourses,
      ],
    },
  },
  externalCoursesDetails: {
    path: '',

    name: 'External Course Details',
    meta: {
      title: 'sidemenu.externalCoursesDetails',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewExternalCourses,
      ],
    },
  },
  externalCoursesStudents: {
    path: '/admin/courses/external/:externalCourseId/students',
    pathFn: ({ externalCourseId }: PathFuncParam) =>
      `/admin/courses/external/${externalCourseId}/students`,
    name: 'External Course students',
    meta: {
      title: 'sidemenu.externalCoursesStudents',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewExternalCourses,
      ],
    },
  },
  specialization: {
    path: '/admin/courses/course-bundle',
    name: 'Course Bundle View',
    meta: {
      title: 'sidemenu.specialization',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewSpecializations,
      ],
    },
  },
  audienceControl: {
    path: '/admin/courses/mine/:courseId/audience-control',
    name: 'Audience Control',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/mine/:courseId/${courseId}/audience-control`,
    meta: {
      title: 'sidemenu.audienceControl',
    },
  },
  courseCertificate: {
    path: '/admin/courses/mine/:courseId/certificate',
    name: 'Course Certificate',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/mine/:courseId/${courseId}/certificate`,
    meta: {
      title: 'sidemenu.certificate',
    },
  },
  importedCourseAudienceControl: {
    path: '/admin/courses/imported/:courseId/audience-control',
    name: ' Imported Course Audience Control',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/imported/${courseId}/audience-control`,

    meta: {
      title: 'sidemenu.audienceControl',
    },
  },
  adminCourseOverview: {
    path: '/admin/courses/mine/:courseId',
    pathFn: ({ courseId }: PathFuncParam) => `/admin/courses/mine/${courseId}`,
    name: 'Admin Course Overview',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  adminCourseDetails: {
    path: '/admin/courses/mine/:courseId/details',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/details`,
    name: 'Admin Course Details',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  adminCourseInstructors: {
    path: '/admin/courses/mine/:courseId/instructors',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/instructors`,
    name: 'Admin Course Instructors',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsers],
    },
  },
  adminCourseStudents: {
    path: '/admin/courses/mine/:courseId/students',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/ `,
    name: 'Admin Course students',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  adminCourseWaitingList: {
    path: '/admin/courses/mine/:courseId/waiting-list',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/waiting-list`,
    name: 'Admin Course Waiting List',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  adminCourseInterestedStudents: {
    path: '/admin/courses/mine/:courseId/interested',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/interested `,
    name: 'Admin Course Interested Students',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  adminCourseFeedbackSurveys: {
    path: '/admin/courses/mine/:courseId/feedback-surveys',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/feedback-surveys `,
    name: 'Admin Course Feedback Surveys',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  adminSingleCourseFeedbackSurvey: {
    path: '/admin/courses/mine/:courseId/feedback-surveys/:feedbackSurveyId',
    pathFn: ({ courseId, feedbackSurveyId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/feedback-surveys/${feedbackSurveyId}`,
    name: 'Admin Course Feedback Survey',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  createFeedbackSurvey: {
    path: '/admin/courses/mine/:courseId/feedback-surveys/create',
    name: 'Admin Create Feedback Survey',
    meta: {
      title: 'feedbackSurvey.createFeedbackSurvey',
    },
  },
  adminCourseFeedbackSurveyDetails: {
    path: '/admin/courses/mine/:courseId/feedback-surveys/:feedbackSurveyId/details',
    pathFn: ({ courseId, feedbackSurveyId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/feedback-surveys/${feedbackSurveyId}/details`,
    name: 'Admin Course Feedback Survey Details',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  adminCourseFeedbackSurveyQuestions: {
    path: '/admin/courses/mine/:courseId/feedback-surveys/:feedbackSurveyId/questions',
    pathFn: ({ courseId, feedbackSurveyId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/feedback-surveys/${feedbackSurveyId}/questions`,
    name: 'Admin Course Feedback Survey Questions',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  adminCourseFeedbackSurveyResult: {
    path: '/admin/courses/mine/:courseId/feedback-surveys/:feedbackSurveyId/result',
    pathFn: ({ courseId, feedbackSurveyId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/feedback-surveys/${feedbackSurveyId}/result`,
    name: 'Admin Course Feedback Survey Result',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
    },
  },
  importedCourseOverview: {
    path: '/admin/courses/imported/:courseId',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/imported/${courseId}`,
    name: 'Imported Course Overview',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewImportedCourses,
      ],
    },
  },
  importedCourseDetails: {
    path: '/admin/courses/imported/:courseId/details',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/imported/${courseId}/details`,
    name: 'Imported Course Details',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewImportedCourses,
      ],
    },
  },
  importedCourseStudents: {
    path: '/admin/courses/imported/:courseId/students',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/imported/${courseId}/students`,
    name: 'Imported Course students',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewImportedCourses,
      ],
    },
  },
  importedCourseInstructors: {
    path: '/admin/courses/imported/:courseId/instructors',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/imported/${courseId}/instructors`,
    name: 'Imported Course Instructors',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewImportedCourses,
      ],
    },
  },
  specializationOverview: {
    path: '/admin/courses/course-bundle',
    pathFn: ({ specializationId }: PathFuncParam) =>
      `/admin/courses/course-bundle/${specializationId}`,
    name: 'Course Bundle Overview',
    meta: {
      title: 'sidemenu.specialization',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewSpecializations,
      ],
    },
  },
  specializationDetails: {
    path: '/admin/courses/course-bundle/:specializationId/details',
    pathFn: ({ specializationId }: PathFuncParam) =>
      `/admin/courses/course-bundle/${specializationId}/details`,
    name: 'Course Bundle Details',
    meta: {
      title: 'sidemenu.specialization',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewSpecializations,
      ],
    },
  },
  specializationCreate: {
    path: '/admin/courses/course-bundle/create',
    name: 'Course Bundle create',
    meta: {
      title: 'sidemenu.specialization',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ModifySpecializations,
      ],
    },
  },
  SpecializationCourses: {
    path: '/admin/courses/course-bundle/:specializationId/courses',
    pathFn: ({ specializationId }: PathFuncParam) =>
      `/admin/courses/course-bundle/${specializationId}/courses`,
    name: 'Course Bundle courses',
    meta: {
      title: 'sidemenu.specialization',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ModifySpecializationCourses,
      ],
    },
  },

  /** >>>>>>>>>>>>>>> Learning Categories Routes <<<<<<<<<<<<<<< **/
  LearningCategories: {
    path: '/admin/learning-categories',
    name: 'Learning Categories',
    meta: {
      title: 'sidemenu.LearningCategories',
      icon: 'mdi-book-multiple',
      hiddenInInternalTeamMode: true,
    },
  },
  SubLearningCategories: {
    path: '/admin/learning-categories/:learningCategoryId',
    pathFn: ({ learningCategoryId }: PathFuncParam) =>
      `/admin/learning-categories/${learningCategoryId}`,
    name: 'Sub Learning Categories',
    meta: {
      title: 'sidemenu.SubLearningCategories',
      icon: 'mdi-book-multiple',
    },
  },
  /** >>>>>>>>>>>>>>> Approvals Routes <<<<<<<<<<<<<<< **/
  approvals: {
    path: '/admin/approvals',
    name: 'Approvals View',
    meta: {
      icon: 'mdi-marker-check',
      title: 'sidemenu.approvals',
      hiddenInInternalTeamMode: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ModifyCourses],
    },
  },

  /** >>>>>>>>>>>>>>> Grades Routes <<<<<<<<<<<<<<< **/
  grades: {
    path: '/admin/grades',
    name: 'Grades View',
    meta: {
      icon: 'mdi-view-module',
      title: 'sidemenu.grades',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCourses],
      // KP-7: grading/results now live in each user's profile (Results tab),
      // so the standalone Grading Schema entry leaves the sidebar under
      // internal-team mode. Nav-only — the route itself stays reachable by
      // URL (deliberately NOT hiddenInInternalTeamMode, which would redirect).
      navHiddenInInternalTeamMode: true,
    },
  },
  gradesOverview: {
    path: '/admin/grades/:gradeId',
    name: 'Grade Schema view',
    pathFn: ({ gradeId }: PathFuncParam) => `admin/grades/${gradeId}`,
    meta: {
      icon: 'mdi-view-module',
      title: 'sidemenu.grades',
    },
  },
  gradesDetails: {
    path: '/admin/grades/:gradeId/details',
    pathFn: ({ gradeId }: PathFuncParam) => `admin/grades/${gradeId}/details`,
    name: 'Grade Schema Details',
    meta: {
      icon: 'mdi-view-module',
      title: 'sidemenu.grades',
    },
  },
  gradesLetters: {
    path: '/admin/grades/:gradeId/letters',
    pathFn: ({ gradeId }: PathFuncParam) => `admin/grades/${gradeId}/letters`,
    name: 'Grade Schema letters',
    meta: {
      icon: 'mdi-view-module',
      title: 'sidemenu.grades',
    },
  },
  createGradeSchema: {
    path: '/admin/grades/create',
    name: 'Grade Schema create',
    meta: {
      icon: 'mdi-view-module',
      title: 'sidemenu.grades',
    },
  },
  gradesCourses: {
    path: '/admin/grades/:gradeId/courses',
    name: 'Grade Schema Courses',
    pathFn: ({ gradeId }: PathFuncParam) => `admin/grades/${gradeId}/courses`,
    meta: {
      icon: 'mdi-view-module',
      title: 'sidemenu.grades',
    },
  },

  /** >>>>>>>>>>>>>>> Social Platform Routes <<<<<<<<<<<<<<< **/
  socialPlatform: {
    path: '/admin/social-platform',
    name: 'Socials Platforms View',
    meta: {
      icon: 'mdi-forum',
      title: 'sidemenu.socialPlatform',
      hiddenInInternalTeamMode: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCommunities],
    },
  },
  viewSocialPlatform: {
    path: '/admin/social-platform',
    name: 'Social Platform View',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCommunities],
    },
  },
  createSocialPlatform: {
    path: '/admin/social-platform/create',
    name: 'Create Group',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ModifyCommunities],
    },
  },
  socialPlatformDetails: {
    path: '/admin/social-platform/:groupId/details',
    name: 'Group Details',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewCommunities],
    },
  },
  viewSocialPlatformMembers: {
    path: '/admin/social-platform/:groupId/members',
    name: 'Group Members',
  },
  /** >>>>>>>>>>>>>>> Events Routes <<<<<<<<<<<<<<< **/
  events: {
    path: '/admin/events',
    name: 'Events View',
    meta: {
      icon: 'mdi-calendar-multiple',
      title: 'sidemenu.events',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewEvents],
    },
  },
  createEvent: {
    path: '/admin/events/create',
    name: 'Create Event',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ModifyEvents],
    },
  },
  eventView: {
    path: '/admin/events',
    name: 'Event View',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewEvents],
    },
  },
  eventDetails: {
    path: '/admin/events/:eventId/details',
    name: 'Event Details',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewEvents],
    },
  },
  eventHosts: {
    path: '/admin/events/:eventId/hosts',
    name: 'Event Hosts',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewEvents],
    },
  },
  eventGuests: {
    path: '/admin/events/:eventId/guests',
    name: 'Event Guests',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewEvents],
    },
  },
  eventEnrollees: {
    path: '/admin/events/:eventId/enrollees',
    name: 'Event Enrollees',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewEvents],
    },
  },
  eventAttendance: {
    path: '/admin/events/:eventId/attendance',
    name: 'Event Attendance',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewEvents],
    },
  },
  eventAttendanceItem: {
    path: '/admin/events/:eventId/attendance/:attendanceItemId',
    name: 'Event Attendance Item',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewEvents],
    },
  },

  eventQrCode: {
    path: '/admin/events/:eventId/qr-code',
    name: 'Event Qr Code',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.TakeSocialEventAttendance,
      ],
    },
  },

  eventTicket: {
    path: '/admin/events/:eventId/ticket',
    name: 'Event Ticket',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewEvents],
    },
  },

  /** >>>>>>>>>>>>>>> Transactions Routes <<<<<<<<<<<<<<< **/
  transactions: {
    path: '/admin/transactions',
    name: 'Transactions View',
    meta: {
      icon: 'mdi-credit-card',
      title: 'sidemenu.transactions',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },
  allTransactions: {
    path: '/admin/transactions/transactions',
    name: 'All Transactions',
    meta: {
      icon: 'mdi-credit-card',
      title: 'sidemenu.transactions',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },
  canceledTransactions: {
    path: '/admin/transactions/canceled-transactions',
    name: 'Canceled Transactions',
    meta: {
      icon: 'mdi-credit-card',
      title: 'sidemenu.transactions',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },
  /** >>>>>>>>>>>>>>> issued Certificates <<<<<<<<<<<<<<< **/
  issuedCertificates: {
    path: '/admin/issued-certificates',
    name: 'Issued Certificates',
    meta: {
      icon: 'mdi-certificate-outline',
      title: 'sidemenu.issuedCertificate',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsersActivity],
      shouldHaveCertificateConfig: true,
    },
  },
  issuedCertificatesLayout: {
    path: '/admin/issued-certificates',
    name: 'Issued Certificates Layout',
    meta: {
      icon: 'mdi-certificate-outline',
      title: 'sidemenu.issuedCertificate',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsersActivity],
    },
  },
  viewIssuedCertificates: {
    path: '/admin/issued-certificates/view-certificate',

    name: 'View Issued Certificate',
    meta: {
      icon: 'mdi-certificate-outline',
      title: 'sidemenu.issuedCertificate',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewUsersActivity],
    },
  },

  /** >>>>>>>>>>>>>>> Permission Routes <<<<<<<<<<<<<<< **/
  permissions: {
    path: '/admin/permissions',
    name: 'Permissions View',
    meta: {
      icon: 'mdi-checkbox-multiple-marked',
      title: 'sidemenu.permissions',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewMemberRoles],
    },
  },

  /** >>>>>>>>>>>>>>> Organization Overview Routes <<<<<<<<<<<<<<< **/
  organizationOverview: {
    path: '/admin/organization-overview',
    name: 'Organization Overview View',
    meta: {
      icon: 'mdi-cog',
      title: 'sidemenu.organizationOverview',
      memberPermissionsAny: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
        EnumOrganizationMemberPermissions.ViewSupportTeams,
        EnumOrganizationMemberPermissions.ViewDynamicPages,
      ],
    },
  },
  supportTickets: {
    path: '/admin/support-tickets',
    name: 'Support Tickets View',
    meta: {
      icon: 'mdi-lifebuoy',
      title: 'sidemenu.supportTickets',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSupportTeams],
      shouldHaveClickupIntegration: true,
    },
  },
  organizationStyle: {
    path: '/admin/organization-overview/style',
    name: 'Organization Style ',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
      ],
    },
  },
  organizationPages: {
    path: '/admin/organization-overview/dynamic-pages',
    name: 'Organization Pages ',
    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewDynamicPages],
    },
  },
  organizationSinglePage: {
    path: '/admin/organization-overview/dynamic-pages/:pageId',
    name: 'Organization Single Page ',

    meta: {
      memberPermissions: [EnumOrganizationMemberPermissions.ViewDynamicPages],
    },
  },

  /** >>>>>>>>>>>>>>>  Support Team Routes  <<<<<<<<<<<<<<< **/
  supportTeam: {
    path: '/admin/organization-overview/support-team',
    name: 'Support Team',
    meta: {
      title: 'sidemenu.supportTeam',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSupportTeams],
    },
  },
  organizationCoursesConfig: {
    path: '/admin/organization-overview/courses',
    name: 'Organization Courses ',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
      ],
    },
  },
  organizationTerminology: {
    path: '/admin/organization-overview/terminology',
    name: 'Organization Terminology ',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
      ],
    },
  },
  organizationShortTerm: {
    path: '/admin/organization-overview/terminology/short-term',
    name: 'Organization Terminology Short Term ',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
      ],
    },
  },
  organizationLongTerm: {
    path: '/admin/organization-overview/terminology/long-term',
    name: 'Organization Terminology Long Term ',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
      ],
    },
  },
  organizationMediaConfig: {
    path: '/admin/organization-overview/media',
    name: 'Organization Media',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
      ],
    },
  },
  organizationCertificateConfig: {
    path: '/admin/organization-overview/certificate',
    name: 'Organization Certificate',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
      ],
    },
  },
  /** >>>>>>>>>>>>>>>  Refunds Routes <<<<<<<<<<<<<<< **/
  refunds: {
    path: '/admin/refunds',
    name: 'Refunds',
    meta: {
      icon: 'mdi-credit-card-refund',
      title: 'sidemenu.refunds',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewRefunds],
    },
  },
  /** >>>>>>>>>>>>>>>  Refunds Routes <<<<<<<<<<<<<<< **/
  transferToBankRequest: {
    path: '/admin/transfer-to-bank-request',
    name: 'Transfer To Bank Request',
    meta: {
      icon: 'mdi-bank',
      title: 'sidemenu.transferToBankRequest',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewWallet],
    },
  },

  /** >>>>>>>>>>>>>>>  NewsLetter  Routes  <<<<<<<<<<<<<<< **/
  newsLetters: {
    path: '/admin/newsLetter',
    name: 'NewsLetter',
    meta: {
      icon: 'mdi-email-newsletter',
      title: 'sidemenu.newsLetter',
      hiddenInInternalTeamMode: true,
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationNewsletter,
      ],
    },
  },
  /** >>>>>>>>>>>>>>>  Registration Request  Routes  <<<<<<<<<<<<<<< **/

  registrationRequest: {
    path: '/admin/users-registration-request',
    name: 'User Registration Request',
    meta: {
      icon: 'mdi-account-badge',
      title: 'sidemenu.registrationRequest',
      memberPermissions: [EnumOrganizationMemberPermissions.ModifyUsers],
      organizationPermissions: [EnumOrganizationPermissions.SelfRegistration],
      shouldHaveRegistrationApproval: true,
    },
  },

  /** >>>>>>>>>>>>>>> announcements Routes <<<<<<<<<<<<<<< **/
  announcements: {
    path: '/admin/announcement',
    name: 'Announcements View',
    meta: {
      icon: 'mdi-bullhorn',
      title: 'sidemenu.announcements',
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
      ],
    },
  },
  createAnnouncement: {
    path: '/admin/announcement/create',
    name: 'Create Announcement',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
        EnumOrganizationMemberPermissions.ModifyOrganizationConfiguration,
      ],
    },
  },
  announcementDetails: {
    path: '/admin/announcement/:announcementId/details',
    name: 'Announcement Details',
    meta: {
      memberPermissions: [
        EnumOrganizationMemberPermissions.ViewOrganizationConfiguration,
      ],
    },
  },

  /** >>>>>>>>>>>>>>> articles Routes <<<<<<<<<<<<<<< **/
  articles: {
    path: '/admin/article',
    name: 'Article View',
    meta: {
      icon: 'mdi-newspaper',
      title: 'sidemenu.articles',
      hiddenInInternalTeamMode: true,
    },
  },
  createArticle: {
    path: '/admin/article/create',
    name: 'Create Article',
  },
  articleDetails: {
    path: '/admin/article/:articleId/details',
    name: 'Article Details',
  },

  /** >>>>>>>>>>>>>>>   Student View  Route  <<<<<<<<<<<<<<< **/

  AdminStudentView: {
    path: '/admin/courses/:courseId/student-view',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/${courseId}/student-view`,
    name: 'Admin Student View',
    meta: {
      title: 'sidebar.studentView',
      icon: 'mdi-glasses',
      requiresAuth: true,
    },
  },
  /** >>>>>>>>>>>>>>>  Wallet  Route  <<<<<<<<<<<<<<< **/
  wallets: {
    path: '/admin/wallets',
    name: 'Wallets',
    meta: {
      icon: 'mdi-wallet-outline',
      title: 'sidebar.wallets',
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewWallet],
    },
  },
  singleWallet: {
    path: '/admin/wallets/:walletId',
    name: 'Wallet View',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewWallet],
    },
  },
  walletLogs: {
    path: '/admin/wallets/:walletId/logs',
    name: 'Wallet Logs',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewWallet],
    },
  },
  walletDetails: {
    path: '/admin/wallets/:walletId/details',
    name: 'Wallet Details',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewWallet],
    },
  },
  conversionRate: {
    path: '/admin/conversion-rate',
    name: 'Conversion Rate',
    meta: {
      icon: 'mdi-cog-transfer-outline',
      title: 'sidebar.conversionRate',
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewConversionRate],
    },
  },
  conversionRateDetails: {
    path: '/admin/conversion-rate/details',
    name: 'Conversion Rate Details',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewConversionRate],
    },
  },
  conversionRateHistory: {
    path: '/admin/conversion-rate/history',
    name: 'Conversion Rate History',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewConversionRate],
    },
  },
  /** >>>>>>>>>>>>>>>  Assessments  Route  <<<<<<<<<<<<<<< **/
  assessments: {
    path: '/admin/assessments',
    name: 'Assessments',
    meta: {
      icon: 'mdi-list-box-outline',
      title: 'sidebar.assessments',
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewAssessments],
      // KP-6: for internal-team orgs, test authoring lives in the Module
      // Planner (quiz builder deep links); the standalone Assessments area
      // is removed from the sidebar only — the routes stay reachable.
      navHiddenInInternalTeamMode: true,
    },
  },
  singleAssessment: {
    path: '/admin/assessments/:assessmentId',
    name: 'Single Assessments',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewAssessments],
    },
  },
  singleAssessmentDetails: {
    path: '/admin/assessments/:assessmentId/details',
    name: 'Single Assessments Details',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewAssessments],
    },
  },
  singleAssessmentOptions: {
    path: '/admin/assessments/:assessmentId/options',
    name: 'Single Assessments Options',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewAssessments],
    },
  },
  createAssessment: {
    path: '/admin/assessments/questions/create',
    name: 'Create Assessments',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewAssessments],
    },
  },
  editAssessment: {
    path: '/admin/assessments/questions/:assessmentId/edit',
    name: 'Edit Assessments',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ModifyAssessments],
    },
  },
  assessmentsResponses: {
    path: '/admin/assessments/responses',
    name: 'Assessments Responses',
    meta: {
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewAssessments],
    },
  },
  assessmentQuestionsStatics: {
    path: '/admin/assessments/questions-statics',
    name: 'Assessments Question Statics',
    meta: {
      icon: 'mdi-list-box-outline',
      title: 'sidebar.question',
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewAssessments],
    },
  },
  assessmentQuestions: {
    path: '/admin/assessments/questions',
    name: 'Assessments Question',
    meta: {
      icon: 'mdi-list-box-outline',
      title: 'sidebar.question',
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewAssessments],
    },
  },
  assessmentQuestionsSkip: {
    path: '/admin/assessments/questions/skipped-summary',
    name: 'Assessments Skipped Summary',
    meta: {
      icon: 'mdi-list-box-outline',
      title: 'sidebar.skipped',
      requiresAuth: true,
      memberPermissions: [EnumOrganizationMemberPermissions.ViewAssessments],
    },
  },

  /** >>>>>>>>>>>>>>>  Transaction Report Tabs <<<<<<<<<<<<<<< **/
  salesRevenue: {
    path: '/admin/transactions/sales-revenue',
    name: 'Sales Revenue',
    meta: {
      title: 'reports.salesRevenue',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },
  programRevenue: {
    path: '/admin/transactions/program-revenue',
    name: 'Program Revenue',
    meta: {
      title: 'reports.programRevenue',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },
  customerInfo: {
    path: '/admin/transactions/customer-info',
    name: 'Customer Info',
    meta: {
      title: 'reports.customerInfo',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },
  /** >>>>>>>>>>>>>>>  Entity Financial Tabs <<<<<<<<<<<<<<< **/
  userFinancial: {
    path: '/admin/users/:userId/financial',
    pathFn: ({ userId }: PathFuncParam) => `/admin/users/${userId}/financial`,
    name: 'User Financial',
    meta: {
      title: 'reports.customerLedger',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },
  courseFinancial: {
    path: '/admin/courses/mine/:courseId/financial',
    pathFn: ({ courseId }: PathFuncParam) =>
      `/admin/courses/mine/${courseId}/financial`,
    name: 'Course Financial',
    meta: {
      title: 'reports.courseRevenue',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },
  eventFinancial: {
    path: '/admin/events/:eventId/financial',
    pathFn: ({ eventId }: PathFuncParam) =>
      `/admin/events/${eventId}/financial`,
    name: 'Event Financial',
    meta: {
      title: 'reports.eventRevenue',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },
  externalCourseFinancial: {
    path: '/admin/courses/external/:externalCourseId/financial',
    pathFn: ({ externalCourseId }: PathFuncParam) =>
      `/admin/courses/external/${externalCourseId}/financial`,
    name: 'External Course Financial',
    meta: {
      title: 'reports.courseRevenue',
      memberPermissions: [EnumOrganizationMemberPermissions.ViewSales],
    },
  },

  /** >>>>>>>>>>>>>>>  Internal Team Routes  <<<<<<<<<<<<<<< **/
  internalTeam: {
    path: '/admin/internal-team',
    name: 'Internal Team View',
    meta: {
      icon: 'mdi-account-group-outline',
      title: 'sidemenu.internalTeam',
      requiresAuth: true,
      shouldHaveInternalTeamMode: true,
      // KP-60: ONE dashboard. The engagement + onboarding widgets this page
      // hosted are folded into WatarDashboard (the Dashboard route), so the
      // separate "Internal Training" nav entry leaves the sidebar. Nav-only —
      // the route stays reachable by URL (deep links, the guided tour).
      navHiddenInInternalTeamMode: true,
    },
  },
  onboardingSteps: {
    path: '/admin/internal-team/onboarding-steps',
    name: 'Onboarding Steps View',
    meta: {
      icon: 'mdi-format-list-numbered',
      title: 'sidemenu.onboardingSteps',
      requiresAuth: true,
      shouldHaveInternalTeamMode: true,
      shouldHaveOnboardingJourney: true,
      organizationPermissions: [EnumOrganizationPermissions.CRUDChecklists],
    },
  },
};
